/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../index';
import { Component } from '@angular/core';
import { LogService, NavigationService, NotifyService, UtilsService } from '../../../services';
import { NotificationModel } from '../../../models';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-notifications-bell-icon',
    templateUrl: './notifications-bell.component.html',
    styleUrls: ['./notifications-bell.component.scss'],
})

export class NotificationsBellComponent extends BaseComponent {
    /**
     * Notifications.
     */
    public notifications: any[] = [];

    /**
     * Notifications shown in the view.
     */
    public htmlRows: any[] = [];

    /**
     * Total number of alerts/notifications
     */
    public totalNotifications: number = 0;

    /**
     * Hold the notification dropdown open during closing notifications
     */
    public forceOpen: boolean = false;

    constructor(
        public navigationService: NavigationService,
        private logService: LogService,
        private notifyService: NotifyService,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        // Get initial notification before subscribing.
        const notifications = this.notifyService.getCopyOfNotifications();
        this.notifications = notifications.slice(0, 9);
        this.totalNotifications = notifications.length;

        // Subscribe to changes to the system notifications
        this.notifyService.updateNotificationsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notifications: NotificationModel[]): void => {
                    this.notifications = notifications.slice(0, 9);
                    this.totalNotifications = notifications.length;
                },
                error: (error: any): void => {
                    this.logService.error(
                        'NotificationsBellComponent: getNotifications',
                        'Error getting notifications',
                        error);
                }
            });
    }

    /**
     * Populate the dropdown's html content
     */
    public populateDropdownRows(): void {
        this.htmlRows = [];
        for (const notification of this.notifications) {
            const { number, duration } = this.utilsService.calculateElapsedTimeToNow(notification.date);
            this.htmlRows.push({
                id: notification.id,
                level: notification.level,
                url: notification.url,
                message: notification.message,
                formattedMessage: this.notifyService.formatNotificationTitle(notification),
                number,
                duration
            });
        }
    }

    /**
     * Navigation to url, prepend appliance ID.
     * @param {string} url
     */
    public navigate(url: string): void {
        if (!url) {
            return;
        }

        const lbUser = JSON.parse(localStorage.getItem('lbUser'));
        const applianceId = lbUser && lbUser.applianceId;
        this.router.navigateByUrl(`/lb/${applianceId}/${url}`).then().catch();
    }

    /**
     * Clear the selected notification.
     * @param id
     */
    public clearSelectedNotification(id: string): void {
        this.forceOpen = this.htmlRows.length > 1;

        const timerId = setTimeout(() => {
            this.notifyService.removeNotificationById(id);
            const deleteIndex = this.htmlRows.findIndex(item => item.id === id);
            if (deleteIndex !== -1) {
                this.htmlRows.splice(deleteIndex, 1);
            }
            this.forceOpen = false;
            clearTimeout(timerId);
        }, 50);
    }
}
